import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import './SyllabusAnalysis.css';
import API_BASE_URL from '../config/api';
import SearchableDropdown from './SearchableDropdown';

const escapeHtml = (text) => String(text)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const formatInlineText = (text) => {
  if (!text) return '';
  const withMath = escapeHtml(text)
    .replace(/\$([^$]+)\$/g, (_, math) => {
      try {
        return katex.renderToString(math, { throwOnError: false, displayMode: false });
      } catch {
        return `$${math}$`;
      }
    });

  return withMath
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`(.+?)`/g, '<code>$1</code>');
};

const formatAnalysisContent = (text) => {
  if (!text) return '';

  const lines = text.split(/\r?\n/);
  let html = '';
  let activeListType = null;
  let sectionOpen = false;
  let skipFollowingSummaryMetric = false;

  const isSummaryMetric = (value) => /^%?\s*\d+(?:[.,]\d+)?(?:\s*\/\s*\d+)?\s*%?$/.test(value);

  const closeList = () => {
    if (activeListType) {
      html += `</${activeListType}>`;
      activeListType = null;
    }
  };

  const ensureSection = () => {
    if (!sectionOpen) {
      html += '<section class="analysis-report-section">';
      sectionOpen = true;
    }
  };

  const closeSection = () => {
    closeList();
    if (sectionOpen) {
      html += '</section>';
      sectionOpen = false;
    }
  };

  lines.forEach((line) => {
    const trimmed = line.trim();

    if (!trimmed) {
      closeList();
      return;
    }

    // Bu iki değer yukarıdaki özet kartlarında zaten gösteriliyor.
    // Ayrıntılı raporda yinelenen tek satırlık skorunu da atla.
    if (skipFollowingSummaryMetric) {
      skipFollowingSummaryMetric = false;
      if (isSummaryMetric(trimmed)) {
        return;
      }
    }

    // Eski rapor biçimi, Markdown başlıkları ve tek başına yazılmış kalın bölüm başlıkları.
    const isStandaloneBoldHeading = /^\*\*[^*]+\*\*:?$/.test(trimmed);
    if (/^={3,}.+={3,}$/.test(trimmed) || /^#{1,6}\s+/.test(trimmed) || isStandaloneBoldHeading) {
      closeSection();
      const heading = trimmed
        .replace(/^=+|=+$/g, '')
        .replace(/^#{1,6}\s+/, '')
        .replace(/^\*\*|\*\*:?$/g, '')
        .trim();
      const normalizedHeading = heading
        .toLocaleLowerCase('tr-TR')
        .replace(/[ç]/g, 'c')
        .replace(/[ğ]/g, 'g')
        .replace(/[ıi]/g, 'i')
        .replace(/[ö]/g, 'o')
        .replace(/[ş]/g, 's')
        .replace(/[ü]/g, 'u')
        .replace(/\s+/g, ' ');
      if (normalizedHeading === 'eslesme orani' || normalizedHeading === 'guncellik skoru') {
        skipFollowingSummaryMetric = true;
        return;
      }
      html += `<section class="analysis-report-section"><h4>${escapeHtml(heading)}</h4>`;
      sectionOpen = true;
      return;
    }

    const bulletMatch = trimmed.match(/^[-*+]\s+(.+)$/);
    const orderedMatch = trimmed.match(/^\d+[.)]\s+(.+)$/);
    if (bulletMatch || orderedMatch) {
      ensureSection();
      const listType = orderedMatch ? 'ol' : 'ul';
      if (activeListType && activeListType !== listType) {
        closeList();
      }
      if (!activeListType) {
        html += `<${listType}>`;
        activeListType = listType;
      }
      const item = (bulletMatch || orderedMatch)[1];
      html += `<li>${formatInlineText(item)}</li>`;
      return;
    }

    closeList();
    ensureSection();
    const paragraph = formatInlineText(trimmed);
    if (/^%?\d+\/\d+$/.test(trimmed) || /^%?\d+/.test(trimmed)) {
      html += `<p class="analysis-key-metric">${paragraph}</p>`;
    } else {
      html += `<p>${paragraph}</p>`;
    }
  });

  closeSection();

  return html;
};

const FormattedInsight = ({ children }) => (
  <span
    className="formatted-insight"
    dangerouslySetInnerHTML={{ __html: formatInlineText(children) }}
  />
);

const SyllabusAnalysis = forwardRef(({
  apiUrl = API_BASE_URL,
  onError = null,
  onLoading = null,
  className = '',
  style = {},
  // Gömülü (CourseDetails içi) kullanım için ek props'lar
  isEmbedded = false,
  preselectedCourseId = null,
  includeWeeklyNotesProp,
  onIncludeWeeklyNotesChange
}, ref) => {
  // State management
  const [availableCourses, setAvailableCourses] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [includeWeeklyNotes, setIncludeWeeklyNotes] = useState(
    typeof includeWeeklyNotesProp === 'boolean' ? includeWeeklyNotesProp : true
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [loadingData, setLoadingData] = useState(false);

  const formattedAnalysis = useMemo(
    () => formatAnalysisContent(result?.analysis),
    [result?.analysis]
  );

  // Utility functions
  const showError = (message) => {
    setError(message);
    if (onError) onError(message);
  };

  const showLoading = (isLoading) => {
    setLoading(isLoading);
    if (onLoading) onLoading(isLoading);
  };

  const hideError = () => {
    setError(null);
  };

  // Dışarıdan gelen includeWeeklyNotes prop'unu state ile senkronize et
  useEffect(() => {
    if (typeof includeWeeklyNotesProp === 'boolean') {
      setIncludeWeeklyNotes(includeWeeklyNotesProp);
    }
  }, [includeWeeklyNotesProp]);

  // Fetch user's assigned courses (sadece bağımsız sayfada)
  useEffect(() => {
    if (isEmbedded) {
      return;
    }
    const fetchCourses = async () => {
      try {
        const akademisyenId = sessionStorage.getItem('akademisyen_id');
        if (!akademisyenId) {
          showError('Giriş yapmanız gerekiyor');
          setLoadingData(false);
          return;
        }
        
        setLoadingData(true);
        // Fetch assigned courses with detailed info
        const coursesRes = await fetch(`${apiUrl}/api/akademisyen/assigned-courses-detailed/${akademisyenId}`);
        const data = await coursesRes.json();
        
        // Backend returns {success: true, courses: [...]} or just [...]
        const courses = data.courses || data;
        
        if (!courses || courses.length === 0) {
          showError('Size atanan ders bulunmamaktadır');
          setAvailableCourses([]);
          return;
        }
        
        // Map backend camelCase to frontend format
        const mappedCourses = courses.map(c => ({
          id: c.courseId || c.course_id,
          course_id: c.courseId || c.course_id,
          program_id: c.programId || c.program_id,
          course_code: c.courseCode || c.course_code,
          course_name: c.courseName || c.course_name,
          program_name: c.programName || c.program_name,
          faculty_name: c.facultyName || c.faculty_name,
          semester: c.semester,
          academic_year: c.academicYear || c.academic_year
        }));
        
        setAvailableCourses(mappedCourses);
      } catch (error) {
        console.error('Dersler yüklenirken hata:', error);
        showError('Dersler yüklenirken hata oluştu');
      } finally {
        setLoadingData(false);
      }
    };
    
    fetchCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, isEmbedded]);

  // Gömülü kullanımda dersi otomatik seç
  useEffect(() => {
    if (isEmbedded && preselectedCourseId) {
      setSelectedCourseId(String(preselectedCourseId));
    }
  }, [isEmbedded, preselectedCourseId]);

  // Analizi gerçekten yapan yardımcı fonksiyon
  const runAnalysis = async (courseIdToUse) => {
    const effectiveCourseId = courseIdToUse || selectedCourseId;

    if (!effectiveCourseId) {
      showError('Lütfen bir ders seçin.');
      return;
    }

    showLoading(true);
    hideError();

    try {
      const response = await fetch(`${apiUrl}/api/syllabus-analysis/courses/${effectiveCourseId}/compare`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          include_weekly_notes: includeWeeklyNotes
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setResult(data);
      } else {
        throw new Error(data.error || 'Ders izlencesi analizi başarısız oldu.');
      }
    } catch (error) {
      console.error('Error:', error);
      showError(error.message || 'Ders izlencesi analizi başarısız oldu.');
    } finally {
      showLoading(false);
    }
  };

  // Form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    await runAnalysis();
  };

  // Dışarıdan Analiz Et çağrısı (CourseDetails içinden)
  useImperativeHandle(ref, () => ({
    startAnalysis: () => {
      const courseIdToUse = preselectedCourseId || selectedCourseId;
      if (!courseIdToUse) {
        showError('Ders bulunamadı. Lütfen sayfayı yenileyin.');
        return;
      }
      setSelectedCourseId(String(courseIdToUse));
      runAnalysis(String(courseIdToUse));
    }
  }));

  const handleReset = () => {
    setSelectedCourseId('');
    setIncludeWeeklyNotes(true);
    setResult(null);
    setError(null);
    setLoading(false);
  };

  const handleIncludeWeeklyNotesChange = (value) => {
    setIncludeWeeklyNotes(value);
    if (onIncludeWeeklyNotesChange) {
      onIncludeWeeklyNotesChange(value);
    }
  };

  // Gömülü kullanımda, henüz analiz başlamadıysa boş alan göstermeyelim
  if (isEmbedded && !result && !loading && !error) {
    return null;
  }

  return (
    <div
      className={`syllabus-analysis-container${isEmbedded ? ' syllabus-analysis-container-embedded' : ''} ${className}`}
      style={style}
    >
      <div className="course-details-container">
        {!isEmbedded && (
          <div className="course-details-header">
            <h1>Ders İzlencesi Analizi</h1>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="error-message">
            <i className="fas fa-exclamation-triangle"></i>
            {' '}{error}
          </div>
        )}

        {/* Yükleniyor mesajı - gömülü kullanımda burada göster */}
        {loading && isEmbedded && (
          <div style={{ textAlign: 'center', marginTop: '10px', color: '#475569' }}>
            <span className="spinner-small"></span>
            {' '}Ders izlencesi analiz ediliyor... Lütfen bekleyin.
          </div>
        )}

        {/* Success Result */}
        {result && (
          <div className="analysis-status" role="status">
            <div className="analysis-status-copy">
              <span className="analysis-status-icon" aria-hidden="true"><i className="fas fa-check"></i></span>
              <div>
                <strong>Analiz hazır</strong>
                <span>Ders izlencesi ve öğrenme çıktıları karşılaştırıldı.</span>
              </div>
            </div>
            {result.total_weekly_notes > 0 && (
              <span className="analysis-note-count">{result.total_weekly_notes} haftalık not dahil</span>
            )}
            <button className="analysis-reset-btn" onClick={handleReset}>
              <i className="fas fa-redo" aria-hidden="true"></i>
              Yeni analiz
            </button>
          </div>
        )}

        {/* Result Content */}
        {result && (
          <div className="result-content">
            <div className="course-details-section analysis-results-panel">
              <div className="analysis-section-heading">
                <div>
                  <span>İzlence değerlendirmesi</span>
                  <h2>Analiz özeti</h2>
                </div>
                <p>Öne çıkan bulguları inceleyin, ardından ayrıntılı değerlendirmeye geçin.</p>
              </div>
              <div className="results-grid" aria-label="Ana analiz göstergeleri">
                <div className="result-card result-card-match">
                  <span className="result-card-label">Öğrenme çıktısı eşleşmesi</span>
                  <strong>{result.match_percentage || 0}<small>%</small></strong>
                  <div className="score-track" aria-hidden="true"><span style={{ width: `${Math.min(Math.max(Number(result.match_percentage) || 0, 0), 100)}%` }} /></div>
                </div>
                <div className="result-card result-card-freshness">
                  <span className="result-card-label">İçerik güncelliği</span>
                  <strong>{result.freshness_score || 0}<small>/10</small></strong>
                  <div className="score-track" aria-hidden="true"><span style={{ width: `${Math.min(Math.max((Number(result.freshness_score) || 0) * 10, 0), 100)}%` }} /></div>
                </div>
              </div>

              {(result.strengths?.length > 0 || result.recommendations?.length > 0) && (
                <div className="insight-grid">
                  {result.strengths?.length > 0 && (
                  <section className="results-section insight-panel insight-panel-positive">
                    <div className="insight-title"><i className="fas fa-check-circle" aria-hidden="true"></i><h3>Güçlü yanlar</h3></div>
                  <ul className="results-list">
                    {result.strengths.map((strength, index) => (
                      <li key={index}><FormattedInsight>{strength}</FormattedInsight></li>
                    ))}
                  </ul>
                  </section>
                  )}

                  {result.recommendations?.length > 0 && (
                  <section className="results-section insight-panel insight-panel-action">
                    <div className="insight-title"><i className="fas fa-lightbulb" aria-hidden="true"></i><h3>Önerilen adımlar</h3></div>
                  <ul className="results-list">
                    {result.recommendations.map((recommendation, index) => (
                      <li key={index}><FormattedInsight>{recommendation}</FormattedInsight></li>
                    ))}
                  </ul>
                  </section>
                  )}
                </div>
              )}

              <div className="analysis-content" aria-labelledby="detailed-analysis-title">
                <div className="analysis-section-heading">
                  <div>
                    <span>Gerekçe ve kapsam</span>
                    <h3 id="detailed-analysis-title">Detaylı değerlendirme</h3>
                  </div>
                </div>
                <p className="analysis-reading-intro">
                  Bulgular, izlencenin kapsamı ve öğrenme çıktılarıyla ilişkisi üzerinden bölüm bölüm açıklanır.
                </p>
                <div className="analysis-detail-meta" aria-label="Değerlendirme yapısı">
                  <span><i className="fas fa-layer-group" aria-hidden="true"></i>Bölümlere ayrılmış bulgular</span>
                  <span><i className="fas fa-book-open" aria-hidden="true"></i>Gerekçe ve öneriler bir arada</span>
                </div>
                <div className="analysis-text">
                  {formattedAnalysis ? (
                    <div
                      className="analysis-html"
                      dangerouslySetInnerHTML={{ __html: formattedAnalysis }}
                    />
                  ) : (
                    'Analiz bulunamadı.'
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form (only show if no result ve bağımsız sayfadaysa) */}
        {!result && !isEmbedded && (
          <div className="course-details-section">
            <h2>Ders Seçimi</h2>
            <form onSubmit={handleSubmit}>
              <table className="course-details-table">
                <tbody>
                  <tr>
                    <td className="table-label">Ders:</td>
                    <td className="table-value">
                      <SearchableDropdown
                        value={selectedCourseId}
                        onChange={setSelectedCourseId}
                        disabled={loadingData}
                        placeholder="Ders seçin..."
                        searchPlaceholder="Ders adı veya koduyla ara…"
                        ariaLabel="Ders"
                        options={availableCourses.map(course => ({
                          value: course.course_id,
                          label: `${course.course_code} - ${course.course_name}${course.program_name ? ` (${course.program_name})` : ''}`,
                          searchText: `${course.course_code || ''} ${course.course_name || ''} ${course.program_name || ''}`,
                        }))}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Weekly Notes Checkbox - Styled separately */}
              <div className="weekly-notes-checkbox-container">
                <label className="weekly-notes-label" htmlFor="includeWeeklyNotes">
                  <input
                    className="weekly-notes-checkbox"
                    type="checkbox"
                    id="includeWeeklyNotes"
                    checked={includeWeeklyNotes}
                    onChange={(e) => handleIncludeWeeklyNotesChange(e.target.checked)}
                  />
                  <span className="checkbox-icon" aria-hidden="true">
                    {includeWeeklyNotes && <i className="fas fa-check"></i>}
                  </span>
                  <span className="checkbox-content">
                    <span className="checkbox-title">
                      Haftalık ders notlarını analize dahil et
                      {includeWeeklyNotes && (
                        <span className="checkbox-badge" aria-hidden="true">
                          <i className="fas fa-check"></i>
                        </span>
                      )}
                    </span>
                    <span className="checkbox-description">
                      Haftalık planları da karşılaştırarak daha ayrıntılı öneriler oluşturur.
                    </span>
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <div className="button-group">
                <button
                  type="submit"
                  className="primary-btn"
                  disabled={loading || !selectedCourseId || loadingData}
                >
                  <>
                    <i className="fas fa-search"></i>
                    Dersi Analiz Et
                  </>
                </button>
              </div>
              {loading && (
                <div style={{ textAlign: 'center', marginTop: '10px', color: '#475569' }}>
                  <span className="spinner-small"></span>
                  Ders izlencesi analiz ediliyor... Lütfen bekleyin.
                </div>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  );
});

export default SyllabusAnalysis;
